import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    addDoc,
    updateDoc,
    onSnapshot,
    orderBy
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import ServiceWorkPanel from '../../components/work/ServiceWorkPanel';
import {
    ArrowLeft,
    MapPin,
    Calendar,
    Clock,
    DollarSign,
    User,
    Phone,
    Mail,
    Loader,
    CheckCircle,
    AlertCircle,
    MessageSquare,
    Send,
    Image as ImageIcon,
    Upload,
    X
} from 'lucide-react';

export default function ContractorJobDetail() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { currentUser, userProfile } = useAuth();
    const messagesEndRef = useRef(null);

    const [job, setJob] = useState(null);
    const [client, setClient] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);

    // Job Listener
    useEffect(() => {
        if (!jobId) return;

        const unsub = onSnapshot(doc(db, 'workOrders', jobId), (docSnap) => {
            if (docSnap.exists()) {
                const data = { id: docSnap.id, ...docSnap.data() };
                setJob(data);

                // Assigned contractors may read the work order but not other users' private profiles.
                setClient({
                    name: data.clientName || 'Client',
                    companyName: data.clientCompany || '',
                    email: data.clientEmail || '',
                    phone: data.clientPhone || '',
                });
            }
            setLoading(false);
        });

        return () => unsub();
    }, [jobId]);

    // Messages Listener
    useEffect(() => {
        if (!jobId) return;

        const q = query(
            collection(db, 'messages'),
            where('workOrderId', '==', jobId),
            orderBy('createdAt', 'asc')
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(msgs);
        });

        return () => unsub();
    }, [jobId]);

    // Auto-scroll messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setSendingMessage(true);

        try {
            await addDoc(collection(db, 'messages'), {
                workOrderId: jobId,
                senderId: currentUser.uid,
                senderName: userProfile?.name || 'Contractor',
                senderType: 'contractor',
                message: newMessage,
                read: false,
                createdAt: new Date().toISOString()
            });

            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (!job) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900">Job Not Found</h2>
                    <button
                        onClick={() => navigate('/contractor/my-jobs')}
                        className="mt-4 text-blue-600 hover:underline"
                    >
                        Return to My Jobs
                    </button>
                </div>
            </div>
        );
    }

    const getStatusColor = (status) => {
        const colors = {
            'assigned': 'bg-blue-100 text-blue-800',
            'scheduled': 'bg-blue-100 text-blue-800',
            'pending_review': 'bg-amber-100 text-amber-800',
            'in-progress': 'bg-yellow-100 text-yellow-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/contractor/my-jobs')}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back to My Jobs</span>
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Job Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                        {job.serviceType}
                                    </h1>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                                        {job.status}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <MapPin className="w-5 h-5" />
                                    <span>{job.location}</span>
                                </div>
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <Calendar className="w-5 h-5" />
                                    <span>{job.schedule?.date || job.preferredDate || 'Not confirmed'}</span>
                                </div>
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <Clock className="w-5 h-5" />
                                    <span>{job.schedule?.startTime ? `${job.schedule.startTime}–${job.schedule.endTime} CT` : 'Service time pending'}</span>
                                </div>
                                {job.contractorPayment && (
                                    <div className="flex items-center space-x-2 text-green-600 font-semibold">
                                        <DollarSign className="w-5 h-5" />
                                        <span>${job.contractorPayment}</span>
                                    </div>
                                )}
                            </div>

                            {job.description && (
                                <div className="mt-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                                    <p className="text-gray-600">{job.description}</p>
                                </div>
                            )}
                        </motion.div>

                        {/* Secure execution / report lifecycle: only Cloud Functions may change status. */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <ServiceWorkPanel job={job} />
                        </motion.div>

                        {/* Messages */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                        >
                            <div className="flex items-center space-x-2 mb-4">
                                <MessageSquare className="w-5 h-5 text-gray-600" />
                                <h3 className="text-lg font-bold text-gray-900">Messages</h3>
                            </div>

                            <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                                {messages.map((msg) => {
                                    const isMe = msg.senderId === currentUser.uid;
                                    return (
                                        <div
                                            key={msg.id}
                                            className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div className={`p-4 rounded-lg max-w-[80%] ${
                                                isMe ? 'bg-blue-50' : 'bg-gray-50'
                                            }`}>
                                                <p className="font-semibold text-sm text-gray-900 mb-1">
                                                    {msg.senderName}
                                                </p>
                                                <p className="text-gray-700">{msg.message}</p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {new Date(msg.createdAt).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            <form onSubmit={handleSendMessage} className="flex space-x-2">
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Type a message..."
                                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                                />
                                <button
                                    type="submit"
                                    disabled={sendingMessage}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </form>
                        </motion.div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Client Info */}
                        {client && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                            >
                                <div className="flex items-center space-x-2 mb-4">
                                    <User className="w-5 h-5 text-blue-600" />
                                    <h3 className="text-lg font-bold text-gray-900">Client</h3>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="font-semibold text-gray-900">
                                            {client.name || client.contactName}
                                        </p>
                                        <p className="text-sm text-gray-600">{client.companyName}</p>
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                        <Mail className="w-4 h-4" />
                                        <span>{client.email}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                        <Phone className="w-4 h-4" />
                                        <span>{client.phone}</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
